import { fg } from '@atlaskit/platform-feature-flags';

import type { Edition, Product } from '../types';
import type { CommerceEntitlementFilter } from '../ui/__generated__/editionAwarenessQuery.graphql';

import { getChargeElementKey } from './get-charge-element-key';
import { getJiraApplicationKey } from './get-jira-application-key';
import { getHamsKey } from './product-to-hams-key';
import { dialogIdToKey } from './use-mark-dialog-as-seen';

/**
 * Returns the variables required for the root Edition Awareness query.
 */
export function getQueryConfig(
	product: Product,
	edition: Edition,
	cloudId: string,
	accountId?: string | null,
	userRolesFromProduct?: string[],
): {
	// AccountID is a required value, but the condition will ensure it is not used if undefined/null
	accountId: string;
	applicationKey: 'JIRA_SOFTWARE' | 'JIRA_PRODUCT_DISCOVERY' | 'JIRA_SERVICE_DESK';
	chargeElement: string;
	cloudId: string;
	// The entitlement filter ensures that we apply the `inTrialOrPreDunning` only when an entitlement is
	// on a non free edition. The filter is based on the trial end timestamp inside of TCS, this means that
	// if a customer downgrades their product to free while on a trial, then they will only show up
	// if inTrialOrPreDunning is set to true as the filter assumes they are still on trial.
	entitlementFilter: CommerceEntitlementFilter | null;
	onboardingStateKeys: string[];
	productKey: string;
	shouldCheckForAddPaymentDetails: boolean;
	shouldCheckForChargeQuantities: boolean;
	shouldCheckForDirectBuyChangeOffering: boolean;
	shouldCheckForPaymentDetails: boolean;
	shouldCheckForStorageLimit: boolean;
	shouldCheckForStorageUsageJira: boolean;
	shouldCheckForTrial: boolean;
	shouldCheckForUserEmail: boolean;
	shouldCheckUserPreferences: boolean;
	shouldSkip: boolean;
} {
	const isProductJiraFree = product === 'jira' && edition === 'free';
	const shouldCheckForPaymentDetails = edition === 'free' && product === 'confluence';

	const shouldCheckUserPreferences = product === 'confluence';

	const isSiteAdmin = userRolesFromProduct?.includes('SITE_ADMIN') ?? false;

	const onboardingStateKeys: string[] = [
		dialogIdToKey('feature-onboarding-modal', product, edition),
	];

	// Entitlement filter controls which entitlements are fetched from the API.
	// The filter is based on the trial end timestamp in TCS - if a customer downgrades to free while
	// on a trial, they still show up when inTrialOrPreDunning is true as the filter assumes they're still on trial.
	//
	//   - Site admin + non-free edition:
	//       Returns entitlements that are (in trial/predunning) OR (in a collection)
	//
	//   - Non-site admin + non-free edition:
	//       Returns entitlements that are (in trial/predunning)
	//
	//   - Free edition (any user):
	//       Returns all entitlements (no filter)
	//
	// GATE OFF (standard behavior):
	//   - Non-free edition: Returns trial/predunning entitlements

	const getEntitlementFilter = () => {
		if (isSiteAdmin && edition !== 'free') {
			return { OR: [{ inTrialOrPreDunning: true }, { hasRelationshipFromWithType: 'COLLECTION' }] };
		} else {
			return edition !== 'free' ? { inTrialOrPreDunning: true } : null;
		}
	};
	const entitlementFilter = getEntitlementFilter();

	return {
		shouldSkip:
			product === 'jira-product-discovery' && fg('jpd_disable_edition_awareness') === true,
		shouldCheckForDirectBuyChangeOffering: isProductJiraFree,
		shouldCheckForTrial: edition === 'premium' || edition === 'standard',
		cloudId,
		productKey: getHamsKey(product),
		shouldCheckForChargeQuantities: edition === 'free',
		entitlementFilter,
		chargeElement: getChargeElementKey(product),
		shouldCheckForStorageUsageJira: isProductJiraFree,
		applicationKey: getJiraApplicationKey(product) ?? 'JIRA_SOFTWARE',
		// AccountID is a required value, but the condition will ensure it is not used if undefined/null
		accountId: accountId ?? '',
		shouldCheckForUserEmail: !!accountId && product === 'jira' && edition === 'standard',
		shouldCheckForStorageLimit: isProductJiraFree,
		shouldCheckForPaymentDetails,
		shouldCheckUserPreferences,
		onboardingStateKeys,
		shouldCheckForAddPaymentDetails:
			edition === 'free' && fg('ea_check_txa_add_payment_details_fg'),
	};
}
